import { TextWithLoadingState } from '@/components/text-with-loading-state';
import { TooltipText } from '@/components/tooltip-text';
import { NucleusVaultMetadata } from '@/constants/nucleus-vaults';
import { ESTIMATED_APY_TOOLTIP_TEXT } from '@/constants/shared';
import { useVaultApy } from '@/hooks/useVaultApy';
import { getIconPathFromTokenSymbol } from '@/utils';
import { Box, FormatNumber, HStack, Icon, Image, Spacer, Text } from '@chakra-ui/react';
import { ReactElement } from 'react';
import { PiChartBarFill } from 'react-icons/pi';

export const IncentivesList = ({ vaultMetadata }: { vaultMetadata: NucleusVaultMetadata }) => {
  const { vaultAddress, apyOverride } = vaultMetadata;

  const { apyData, isLoadingApyData } = useVaultApy({ vaultAddress });
  const { underlyingRate = 0, wPlumeApy = 0 } = apyData ?? {};

  return (
    <Box spaceY={2}>
      <Incentive
        icon={
          <Icon boxSize={6} color="fg.muted">
            <PiChartBarFill />
          </Icon>
        }
        apy={underlyingRate}
        title="Base APY"
        subText="Optimizer Vault Yield"
        loading={isLoadingApyData}
        tooltip={apyOverride ? ESTIMATED_APY_TOOLTIP_TEXT : undefined}
      />
      <Incentive
        icon={<Image src={getIconPathFromTokenSymbol('WPLUME')} />}
        title="PLUME Incentives"
        apy={wPlumeApy}
        subText="Royco Boosted Yield"
        loading={isLoadingApyData}
      />
    </Box>
  );
};

const Incentive = ({
  icon,
  apy,
  title,
  subText,
  loading,
  tooltip,
}: {
  icon: ReactElement;
  apy: number;
  title: string;
  subText: string;
  loading: boolean;
  tooltip?: string;
}) => {
  const IncentiveApyText = () => (
    <TextWithLoadingState fontWeight="500" color="fg" loading={loading}>
      <FormatNumber style="percent" maximumFractionDigits={0} value={apy} />
    </TextWithLoadingState>
  );
  return (
    <HStack py={2} gap={4}>
      {icon && icon}
      <Box>
        <Text fontWeight="500">{title}</Text>
        <Text fontSize="xs" color="fg.subtle" fontWeight="500">
          {subText}
        </Text>
      </Box>
      <Spacer flex={1} />
      <TooltipText content={tooltip} text={<IncentiveApyText />} />
    </HStack>
  );
};
